import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import { getSchoolIdForUser } from "../../utils/userContext";
import { fetchTeacherMaterialRequests, submitMaterialRequest } from "../../utils/termBudgetApi";
import { useTeacherTeaching } from "../../hooks/useTeacherTeaching";
import Popup from "../../components/Popup";

const ITEM_TYPES = [
    { value: "LearningMaterial", label: "Learning material" },
    { value: "LabSupply", label: "Lab supply" },
    { value: "Stationery", label: "Stationery" },
    { value: "Equipment", label: "Equipment" },
    { value: "Other", label: "Other" },
];

function statusChip(status) {
    if (status === "Approved") return <Chip size="small" color="success" label="Approved" />;
    if (status === "Fulfilled") return <Chip size="small" color="info" label="Fulfilled" />;
    if (status === "Rejected") return <Chip size="small" color="error" label="Rejected" />;
    return <Chip size="small" color="warning" label="Pending review" />;
}

export default function TeacherMaterialRequests() {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = getSchoolIdForUser(currentUser);
    const { activeSubjectId: subjectId, activeClassId: classId, subjectName, className } = useTeacherTeaching();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const [form, setForm] = useState({
        title: "",
        description: "",
        itemType: "LearningMaterial",
        quantity: "1",
        urgency: "Normal",
    });

    const load = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const result = await fetchTeacherMaterialRequests(teacherId);
            setData(result);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load your requests.");
            setShowPopup(true);
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    useEffect(() => {
        load();
    }, [load]);

    const periodLabel = data?.period
        ? [data.period.term, data.period.academicYear].filter(Boolean).join(" · ")
        : "Current term";

    const handleSubmit = async () => {
        if (!form.title.trim()) {
            setMessage("Please describe what you need.");
            setShowPopup(true);
            return;
        }
        setSaving(true);
        try {
            await submitMaterialRequest({
                schoolId,
                teacherId,
                subjectId,
                classId,
                title: form.title.trim(),
                description: form.description.trim(),
                itemType: form.itemType,
                quantity: form.quantity,
                urgency: form.urgency,
            });
            setDialogOpen(false);
            setForm({ title: "", description: "", itemType: "LearningMaterial", quantity: "1", urgency: "Normal" });
            setMessage("Request submitted. The bursar or director will review it — you will not see budget amounts.");
            setShowPopup(true);
            load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit request.");
            setShowPopup(true);
        } finally {
            setSaving(false);
        }
    };

    const rows = data?.requests || [];

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.info.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md">
                <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <InventoryOutlinedIcon color="primary" />
                    <Box flex={1}>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            Supplies
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            Material requests
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Request learning materials for {className} · {subjectName}. Budget details are not shown here.
                        </Typography>
                        <Chip size="small" label={periodLabel} variant="outlined" sx={{ mt: 1 }} />
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                        New request
                    </Button>
                </Stack>

                <Alert severity="info" sx={{ mb: 2 }}>
                    Submit what your class needs this term. The bursar or director reviews requests — you only see approval status, not costs or budgets.
                </Alert>

                {loading ? (
                    <Stack alignItems="center" py={6}><CircularProgress /></Stack>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Qty</TableCell>
                                    <TableCell>Urgency</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Feedback</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                            No requests yet for this term.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => (
                                        <TableRow key={row.id || row._id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{row.title}</Typography>
                                                {row.description ? (
                                                    <Typography variant="caption" color="text.secondary" display="block">{row.description}</Typography>
                                                ) : null}
                                            </TableCell>
                                            <TableCell>{row.itemType}</TableCell>
                                            <TableCell>{row.quantity}</TableCell>
                                            <TableCell>{row.urgency}</TableCell>
                                            <TableCell>{statusChip(row.status)}</TableCell>
                                            <TableCell>
                                                {row.status === "Rejected" && row.rejectionReason ? row.rejectionReason : "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>

            <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Request materials</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField
                            fullWidth
                            required
                            label="What do you need?"
                            placeholder="e.g. A4 ruled exercise books for Form 2 Maths"
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                        />
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label="Details (optional)"
                            value={form.description}
                            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select label="Type" value={form.itemType} onChange={(e) => setForm((f) => ({ ...f, itemType: e.target.value }))}>
                                {ITEM_TYPES.map((t) => (
                                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Quantity"
                                value={form.quantity}
                                onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))}
                                inputProps={{ min: 1 }}
                            />
                            <FormControl fullWidth>
                                <InputLabel>Urgency</InputLabel>
                                <Select label="Urgency" value={form.urgency} onChange={(e) => setForm((f) => ({ ...f, urgency: e.target.value }))}>
                                    <MenuItem value="Normal">Normal</MenuItem>
                                    <MenuItem value="Urgent">Urgent</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}>Submit request</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
